/**
 * Staff and roles (D74, D77) — Owner only. Add employees with a temporary password, give them a
 * role, disable them, reset their password or 2FA; create and edit custom roles. Owner-only
 * powers are shown but can't be ticked. Every change needs a fresh 2FA code and is audited.
 */
import type { AdminRoleView, AdminStaffMember } from "@custos/api-client";
import { ADMIN_PERMISSIONS, ALL_ADMIN_PERMISSIONS, type AdminPermission } from "@custos/core";
import {
  Badge,
  Button,
  Card,
  CardTitle,
  CopyButton,
  EmptyState,
  Field,
  Input,
  Notice,
  Select,
  Skeleton,
  Spot,
  formatDateTime,
} from "@custos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { PageHeader } from "../components/AdminShell";
import { StepUpModal } from "../components/StepUpModal";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { qk, useAdminMe } from "../lib/queries";

const MIN = 12;
const GROUPS = ["View", "Act", "Owner"] as const;
const GROUP_LABEL = { View: "See", Act: "Do", Owner: "Owner only (fixed)" } as const;

/** A random temporary password the Owner hands to the employee (it must be changed at first login). */
function generatePassword(): string {
  const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

const useStaff = () => useQuery({ queryKey: qk.staff, queryFn: () => api.staff() });
const useRoles = () => useQuery({ queryKey: qk.roles, queryFn: () => api.roles() });

function useRefresh() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.staff }),
      queryClient.invalidateQueries({ queryKey: qk.roles }),
      queryClient.invalidateQueries({ queryKey: qk.me }),
    ]);
}

function TempPasswordField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field
      label="Temporary password"
      hint={`At least ${MIN} characters. Give it to the employee privately; they must change it at first login.`}
      error={value.length > 0 && value.length < MIN ? `At least ${MIN} characters` : null}
    >
      {(p) => (
        <div className="flex gap-2">
          <Input
            {...p}
            autoComplete="off"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="font-mono"
          />
          <Button type="button" variant="secondary" onClick={() => onChange(generatePassword())}>
            Generate
          </Button>
          {value.length >= MIN && <CopyButton value={value} label="Copy temporary password" />}
        </div>
      )}
    </Field>
  );
}

export function StaffPage() {
  const staff = useStaff();
  const roles = useRoles();
  const [adding, setAdding] = useState(false);
  const [roleEdit, setRoleEdit] = useState<AdminRoleView | "new" | null>(null);

  return (
    <>
      <PageHeader
        title="Staff"
        subtitle="Employees, their roles and what each role may do. Only the Owner sees this page."
        actions={
          <Button onClick={() => setAdding(true)} disabled={!roles.data}>
            <Plus className="size-4" aria-hidden /> Add employee
          </Button>
        }
      />
      {(staff.error ?? roles.error) && (
        <Notice tone="danger">{errorMessage(staff.error ?? roles.error)}</Notice>
      )}

      <section aria-label="Staff members">
        {staff.isPending ? (
          <Skeleton className="h-40 rounded-card" />
        ) : (
          <ul className="stagger grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="staff-list">
            {staff.data?.map((m) => (
              <StaffCard key={m.id} member={m} roles={roles.data ?? []} />
            ))}
          </ul>
        )}
      </section>

      <div className="mt-8 mb-4 flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-heading text-2xl font-medium text-fg">Roles</h2>
        <Button variant="secondary" onClick={() => setRoleEdit("new")}>
          <Plus className="size-4" aria-hidden /> New role
        </Button>
      </div>
      {roles.isPending ? (
        <Skeleton className="h-40 rounded-card" />
      ) : roles.data?.length === 0 ? (
        <Card>
          <EmptyState icon={<Spot name="lock" />} title="No roles" />
        </Card>
      ) : (
        <ul className="stagger grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="role-list">
          {roles.data?.map((r) => (
            <RoleCard key={r.id} role={r} onEdit={() => setRoleEdit(r)} />
          ))}
        </ul>
      )}

      <AddStaffModal open={adding} onOpenChange={setAdding} roles={roles.data ?? []} />
      <RoleModal role={roleEdit} onClose={() => setRoleEdit(null)} />
    </>
  );
}

function StaffCard({ member, roles }: { member: AdminStaffMember; roles: AdminRoleView[] }) {
  const me = useAdminMe();
  const refresh = useRefresh();
  const [action, setAction] = useState<"role" | "disable" | "password" | "2fa" | null>(null);
  const [roleId, setRoleId] = useState(member.role.id);
  const [temp, setTemp] = useState("");
  const isMe = me.data?.id === member.id;
  const disabled = member.status === "disabled";

  return (
    <li>
      <Card className="flex h-full flex-col gap-3" data-testid={`staff-${member.email}`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-medium text-fg">
              {member.email} {isMe && <span className="text-muted">(you)</span>}
            </p>
            <p className="text-xs text-muted">
              Added {formatDateTime(member.createdAt)} · last login{" "}
              {member.lastLoginAt ? formatDateTime(member.lastLoginAt) : "never"}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge tone={member.role.isOwner ? "primary" : "neutral"}>{member.role.name}</Badge>
            <Badge tone={disabled ? "danger" : member.status === "invited" ? "warning" : "success"}>
              {member.status === "invited" ? "2FA not set up" : member.status}
            </Badge>
            {member.mustChangePassword && <Badge tone="warning">temporary password</Badge>}
          </div>
        </div>
        <div className="mt-auto flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setRoleId(member.role.id);
              setAction("role");
            }}
          >
            Change role
          </Button>
          <Button size="sm" variant={disabled ? "primary" : "danger"} onClick={() => setAction("disable")}>
            {disabled ? "Enable" : "Disable"}
          </Button>
          {!isMe && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setTemp("");
                  setAction("password");
                }}
              >
                Reset password
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setAction("2fa")}>
                Reset 2FA
              </Button>
            </>
          )}
        </div>
      </Card>

      <StepUpModal
        open={action === "role"}
        onOpenChange={(o) => !o && setAction(null)}
        title={`Role for ${member.email}`}
        description="It applies straight away, also to sessions that are open now."
        confirmLabel="Save role"
        canConfirm={roleId !== member.role.id}
        onConfirm={(code) => api.setStaffRole(member.id, roleId, code)}
        onDone={refresh}
      >
        <Field label="Role">
          {(p) => (
            <Select {...p} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </StepUpModal>
      <StepUpModal
        open={action === "disable"}
        onOpenChange={(o) => !o && setAction(null)}
        title={disabled ? `Enable ${member.email}?` : `Disable ${member.email}?`}
        description={disabled ? "They can log in again." : "They are logged out at once and can't log in."}
        confirmLabel={disabled ? "Enable" : "Disable"}
        danger={!disabled}
        onConfirm={(code) => api.setStaffDisabled(member.id, !disabled, code)}
        onDone={refresh}
      />
      <StepUpModal
        open={action === "password"}
        onOpenChange={(o) => !o && setAction(null)}
        title={`New temporary password for ${member.email}`}
        description="They are logged out and must choose their own password at the next login."
        confirmLabel="Set temporary password"
        canConfirm={temp.length >= MIN}
        onConfirm={(code) => api.resetStaffPassword(member.id, temp, code)}
        onDone={refresh}
      >
        <TempPasswordField value={temp} onChange={setTemp} />
      </StepUpModal>
      <StepUpModal
        open={action === "2fa"}
        onOpenChange={(o) => !o && setAction(null)}
        title={`Reset 2FA for ${member.email}?`}
        description="For a lost phone. They are logged out and set up 2FA again at the next login."
        confirmLabel="Reset 2FA"
        danger
        onConfirm={(code) => api.resetStaff2fa(member.id, code)}
        onDone={refresh}
      />
    </li>
  );
}

function AddStaffModal({
  open,
  onOpenChange,
  roles,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  roles: AdminRoleView[];
}) {
  const refresh = useRefresh();
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [temp, setTemp] = useState("");
  const firstCustom = roles.find((r) => !r.isOwner)?.id ?? roles[0]?.id ?? "";
  const role = roleId || firstCustom;
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  useEffect(() => {
    if (!open) return;
    setEmail("");
    setRoleId("");
    setTemp(generatePassword());
  }, [open]);

  return (
    <StepUpModal
      open={open}
      onOpenChange={onOpenChange}
      title="Add employee"
      description="Their first login sets up 2FA, then asks for their own password."
      confirmLabel="Add employee"
      canConfirm={emailOk && temp.length >= MIN && Boolean(role)}
      onConfirm={(code) => api.createStaff({ email: email.trim(), roleId: role, tempPassword: temp }, code)}
      onDone={refresh}
    >
      <Field label="Email">
        {(p) => (
          <Input
            {...p}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
      </Field>
      <Field label="Role">
        {(p) => (
          <Select {...p} value={role} onChange={(e) => setRoleId(e.target.value)}>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {roles.find((r) => r.id === role)?.isOwner && (
        <Notice tone="warning">Owner = every power, including the signer, cold wallet and staff.</Notice>
      )}
      <TempPasswordField value={temp} onChange={setTemp} />
    </StepUpModal>
  );
}

function RoleCard({ role, onEdit }: { role: AdminRoleView; onEdit: () => void }) {
  const refresh = useRefresh();
  const [deleting, setDeleting] = useState(false);
  return (
    <li>
      <Card className="flex h-full flex-col gap-3" data-testid={`role-${role.name}`}>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            {role.isOwner && <Lock className="size-4" aria-hidden />}
            {role.name}
          </CardTitle>
          <Badge>{role.staffCount} staff</Badge>
        </div>
        {role.description && <p className="text-sm text-muted">{role.description}</p>}
        <ul className="flex flex-wrap gap-1.5">
          {role.isOwner ? (
            <li>
              <Badge tone="primary">everything</Badge>
            </li>
          ) : role.permissions.length === 0 ? (
            <li className="text-sm text-muted">No permissions</li>
          ) : (
            role.permissions.map((p) => (
              <li key={p}>
                <Badge>{ADMIN_PERMISSIONS[p as AdminPermission]?.label ?? p}</Badge>
              </li>
            ))
          )}
        </ul>
        {!role.isOwner && (
          <div className="mt-auto flex gap-2">
            <Button size="sm" variant="secondary" onClick={onEdit}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setDeleting(true)}
              disabled={role.staffCount > 0}
            >
              Delete
            </Button>
          </div>
        )}
      </Card>
      <StepUpModal
        open={deleting}
        onOpenChange={setDeleting}
        title={`Delete the role "${role.name}"?`}
        confirmLabel="Delete role"
        danger
        onConfirm={(code) => api.deleteRole(role.id, code)}
        onDone={refresh}
      />
    </li>
  );
}

function RoleModal({ role, onClose }: { role: AdminRoleView | "new" | null; onClose: () => void }) {
  const refresh = useRefresh();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [perms, setPerms] = useState<string[]>([]);
  const [loadedFor, setLoadedFor] = useState<AdminRoleView | "new" | null>(null);
  if (role !== loadedFor) {
    setLoadedFor(role);
    setName(role && role !== "new" ? role.name : "");
    setDescription(role && role !== "new" ? role.description : "");
    setPerms(role && role !== "new" ? role.permissions : []);
  }
  const toggle = (p: string) =>
    setPerms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  const isNew = role === "new";
  const editing = role && role !== "new" ? role : null;

  return (
    <StepUpModal
      open={role !== null}
      onOpenChange={(o) => !o && onClose()}
      title={editing ? `Edit role "${editing.name}"` : "New role"}
      description="Changes apply at once to everyone with this role."
      confirmLabel={isNew ? "Create role" : "Save role"}
      canConfirm={name.trim().length > 0}
      onConfirm={(code) => {
        const input = { name: name.trim(), description: description.trim(), permissions: perms };
        return editing ? api.updateRole(editing.id, input, code) : api.createRole(input, code);
      }}
      onDone={refresh}
    >
      <Field label="Name">
        {(p) => <Input {...p} value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label="Description (optional)">
        {(p) => (
          <Input
            {...p}
            value={description}
            maxLength={200}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </Field>
      <div className="flex max-h-[45dvh] flex-col gap-4 overflow-y-auto pr-1" data-testid="role-permissions">
        {GROUPS.map((g) => (
          <fieldset key={g} className="flex flex-col gap-1">
            <legend className="mb-1 text-xs font-medium tracking-wide text-muted uppercase">
              {GROUP_LABEL[g]}
            </legend>
            {ALL_ADMIN_PERMISSIONS.filter((p) => ADMIN_PERMISSIONS[p].group === g).map((p) => {
              const ownerOnly = ADMIN_PERMISSIONS[p].ownerOnly;
              return (
                <label
                  key={p}
                  className={`flex min-h-11 items-center gap-3 text-sm ${ownerOnly ? "cursor-not-allowed text-muted" : "cursor-pointer text-fg"}`}
                >
                  <input
                    type="checkbox"
                    className="size-5 shrink-0 accent-[var(--primary)]"
                    checked={!ownerOnly && perms.includes(p)}
                    disabled={ownerOnly}
                    onChange={() => toggle(p)}
                  />
                  {ADMIN_PERMISSIONS[p].label}
                  {ownerOnly && <Lock className="ml-auto size-4 shrink-0" aria-label="Owner only" />}
                </label>
              );
            })}
          </fieldset>
        ))}
      </div>
    </StepUpModal>
  );
}
